import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type WorkspaceId = 'aws' | 'anthropic';
export type WorkspaceStatus = 'active' | 'pilot' | 'archived';

export interface WorkspaceOption {
  id: WorkspaceId;
  name: string;
  status: WorkspaceStatus;
  tagline: string;
}

export const WORKSPACE_STORAGE_KEY = 'starmap.workspaceId';
export const DEFAULT_WORKSPACE_ID: WorkspaceId = 'aws';

export const WORKSPACES: WorkspaceOption[] = [
  { id: 'aws', name: 'AWS', status: 'active', tagline: 'Relationship Intelligence' },
  { id: 'anthropic', name: 'Anthropic', status: 'pilot', tagline: 'Pilot Workspace' },
];

interface WorkspaceState {
  workspace: WorkspaceOption;
  workspaceId: WorkspaceId;
  workspaces: WorkspaceOption[];
  isLiveWorkspace: boolean;
  setWorkspaceId: (id: WorkspaceId) => void;
}

const Ctx = createContext<WorkspaceState | null>(null);

export function isWorkspaceId(value: string | null): value is WorkspaceId {
  return value === 'aws' || value === 'anthropic';
}

function readStoredWorkspace(): WorkspaceId {
  try {
    const stored = window.localStorage.getItem(WORKSPACE_STORAGE_KEY);
    return isWorkspaceId(stored) ? stored : DEFAULT_WORKSPACE_ID;
  } catch {
    return DEFAULT_WORKSPACE_ID;
  }
}

function readRouteWorkspace(): WorkspaceId | null {
  try {
    const routeId = window.location.pathname.match(/^\/app\/([^/]+)/)?.[1] ?? null;
    return isWorkspaceId(routeId) ? routeId : null;
  } catch {
    return null;
  }
}

function readInitialWorkspace(): WorkspaceId {
  return readRouteWorkspace() ?? readStoredWorkspace();
}

function writeStoredWorkspace(id: WorkspaceId) {
  try {
    window.localStorage.setItem(WORKSPACE_STORAGE_KEY, id);
  } catch {
    // Locked-down storage should not block workspace selection.
  }
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [workspaceId, setWorkspaceIdState] = useState<WorkspaceId>(readInitialWorkspace);

  useEffect(() => {
    writeStoredWorkspace(workspaceId);
  }, [workspaceId]);

  const setWorkspaceId = useCallback((id: WorkspaceId) => {
    setWorkspaceIdState(id);
  }, []);

  const workspace = useMemo(
    () => WORKSPACES.find((option) => option.id === workspaceId) ?? WORKSPACES[0]!,
    [workspaceId],
  );

  const value = useMemo<WorkspaceState>(
    () => ({
      workspace,
      workspaceId,
      workspaces: WORKSPACES,
      isLiveWorkspace: workspaceId === 'aws',
      setWorkspaceId,
    }),
    [setWorkspaceId, workspace, workspaceId],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useWorkspace(): WorkspaceState {
  const value = useContext(Ctx);
  if (!value) throw new Error('useWorkspace must be used inside <WorkspaceProvider>');
  return value;
}
